// ============================================
// INFRASTRUCTURE - Flow Repository
// ============================================

import axios, { AxiosInstance } from 'axios';
import type { 
  FlowCustomer, 
  FlowSubscription, 
  FlowInvoice,
  CreateSubscriptionRequest,
  CreateSubscriptionResponse,
  SubscriptionStatusResponse,
  RegisterCreditCardRequest,
  RegisterCreditCardResponse,
  CreateSubscriptionWithCreditCardRequest,
  CreateSubscriptionWithCreditCardResponse,
  FlowPlan,
  FlowSubscriptionDetail,
  CancelSubscriptionRequest,
  CancelSubscriptionResponse,
  FlowCupon,
} from '../../domain/entities';

const FLOW_API_BASE_URL = import.meta.env?.VITE_FLOW_API_BASE_URL || 'http://localhost:8082';

// Instancia de Axios específica para Flow API
const flowClient: AxiosInstance = axios.create({
  baseURL: FLOW_API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

export const flowRepository = {
  /**
   * Obtener información del cliente en Flow
   */
  async getCustomer(rutAlmacen: string): Promise<FlowCustomer> {
    // Remover DV y guion (ej: "17728133-6" -> "17728133")
    const rutSinDv = rutAlmacen.split('-')[0];
    console.log('Obteniendo cliente de Flow para RUT:', rutSinDv);
    console.log('URL de Flow API:', `${FLOW_API_BASE_URL}/v2/flow/customer?rut_almacen=${rutSinDv}`);
    
    const { data } = await flowClient.get<FlowCustomer>(
      `/v2/flow/customer?rut_almacen=${rutSinDv}`
    );
    return data;
  },

  /**
   * Obtener información de la suscripción
   */
  async getSubscription(subscriptionId: string): Promise<FlowSubscription> {
    const { data } = await flowClient.get<FlowSubscription>(
      `/v2/flow/subscription/${subscriptionId}`
    );
    return data;
  },

  /**
   * Obtener detalle completo de la suscripción
   */
  async getSubscriptionDetail(subscriptionId: string): Promise<FlowSubscriptionDetail> {
    const { data } = await flowClient.get<FlowSubscriptionDetail>(
      `/v2/flow/subscription/detail/${subscriptionId}`
    );
    return data;
  },

  /**
   * Obtener información de la factura
   */
  async getInvoice(invoiceId: number): Promise<FlowInvoice> {
    const { data } = await flowClient.get<FlowInvoice>(
      `/v2/flow/invoice/${invoiceId}`
    );
    return data;
  },

  /**
   * Crear una nueva suscripción (manual) en Flow
   */
  async createSubscription(request: CreateSubscriptionRequest): Promise<CreateSubscriptionResponse> {
    const { data } = await flowClient.post<CreateSubscriptionResponse>(
      '/v2/flow/subcription/plan',
      request
    );
    return data;
  },

  /**
   * Consultar el estado de una orden de suscripción
   */
  async getSubscriptionStatus(rutAlmacen: string): Promise<SubscriptionStatusResponse> {
    // Remover DV y guion (ej: "17728133-6" -> "17728133")
    const rutSinDv = rutAlmacen.split('-')[0];
    const { data } = await flowClient.get<SubscriptionStatusResponse>(
      `/v2/flow/status?rut_almacen=${rutSinDv}`
    );
    return data;
  },

  /**
   * Registrar tarjeta de crédito en Flow
   */
  async registerCreditCard(request: RegisterCreditCardRequest): Promise<RegisterCreditCardResponse> {
    const { data } = await flowClient.post<RegisterCreditCardResponse>(
      '/v2/flow/customer/register/creditcard',
      request
    );
    return data;
  },

  /**
   * Crear suscripción con tarjeta de crédito ya registrada
   */
  async createSubscriptionWithCreditCard(request: CreateSubscriptionWithCreditCardRequest): Promise<CreateSubscriptionWithCreditCardResponse> {
    const { data } = await flowClient.post<CreateSubscriptionWithCreditCardResponse>(
      '/v2/flow/subcription/plan/creditcard',
      request
    );
    return data;
  },

  /**
   * Obtener información de un plan de suscripción
   */
  async getPlan(planId: number): Promise<FlowPlan> {
    const { data } = await flowClient.get<FlowPlan>(
      `/v2/flow/plan?planId=${planId}`
    );
    return data;
  },

  /**
   * Cancelar una suscripción
   */
  async cancelSubscription(request: CancelSubscriptionRequest): Promise<CancelSubscriptionResponse> {
    const { data } = await flowClient.post<CancelSubscriptionResponse>(
      '/v2/flow/subcription/plan/cancel',
      request
    );
    return data;
  },

  /**
   * Buscar un cupón de descuento por código
   */
  async getCupon(codigo: string): Promise<FlowCupon> {
    const { data } = await flowClient.get<FlowCupon>(
      `/v2/flow/cupon/${codigo}`
    );
    return data;
  },
};
